import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { MatLegacyPaginator as MatPaginator } from '@angular/material/legacy-paginator';
import { Router } from '@angular/router';
import { Lightbox } from 'ngx-lightbox';
import * as moment from 'moment';
import { TourHelper } from 'src/app/helpers/tour-helper';
import DateHelper from 'src/app/helpers/date-helper';
import Employee from 'src/app/model/product/employee';
import { SubProjectImmoAppointment } from 'src/app/model/project/sub-project-immo-appointment';
import { SubProjectControllerRequestService } from 'src/app/services/api/sub-project-controller-request.service';
import { FilterStoreService } from 'src/app/services/filter-store.service';
import { ModalService } from 'src/app/services/modal.service';
import { PageIndexService } from 'src/app/services/page-index-service';
import { EmployeeTourEditModalComponent } from '../../../tour/tour-page/employee-tour-edit-modal/employee-tour-edit-modal.component';
import { IPInstallerListControllerRequestService } from '../../../tour/tour-page/service/ip-installer-list-controller-request.service';
import { AbstractImmoOverviewComponent } from '../../abstract-immo-overview.component';

@Component({
  selector: 'appointment-new-immo-overview',
  templateUrl: '../../abstract-immo-overview.component.html',
  styleUrls: ['../../abstract-immo-overview.component.scss'],
})
export class AppointmentNewImmoOverviewComponent
  extends AbstractImmoOverviewComponent<SubProjectImmoAppointment>
  implements OnInit
{
  @Output()
  countChange = new EventEmitter<number>();

  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;

  private allPlacers: Employee[];

  constructor(
    protected filterStoreService: FilterStoreService,
    protected pageIndexService: PageIndexService,
    protected cd: ChangeDetectorRef,
    protected subProjectService: SubProjectControllerRequestService,
    protected ipInstallerListControllerRequestService: IPInstallerListControllerRequestService,
    protected modalService: ModalService,
    protected _lightbox: Lightbox,
    protected router: Router
  ) {
    super(
      [
        {
          key: 'project__id',
          active: false,
          strictEquality: true,
          numeric: true,
        },
        {
          key: 'immoNewCustomerName',
          active: false,
          strictEquality: false,
          numeric: false,
        },
      ],
      filterStoreService,
      pageIndexService,
      cd,
      subProjectService,
      _lightbox,
      router
    );
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.ipInstallerListControllerRequestService
      .IPInstallerList()
      .then((placers: Employee[]) => {
        this.allPlacers = placers;
        this.placersLoaded = true;
      });
  }

  public copyProjectId(subProject: SubProjectImmoAppointment): void {
    throw new Error('Method not implemented.');
  }

  protected getPaginator(): MatPaginator {
    return this.paginator;
  }

  protected getLocalStorageKey(): string {
    return 'AppointmentNewImmoOverviewComponent';
  }

  protected initDisplayedColumns(): void {
    this.displayedColumns = [
      'projectId',
      'placementDate',
      'installer',
      'route',
      'customerName',
      'address',
      'kind',
      'measurements',
      'type',
      'placementFloor',
      'collectKey',
      'displayStatus',
    ];
  }

  protected initSortSettings(): void {
    this.sortColumn = 'currentConfirmedPlacementDate';
    this.sortAscending = true;
  }

  protected fetchItems() {
    this.loading = true;
    this.items = [];

    this.subProjectService
      .getImmoAppointment(
        this.generateFilterString(),
        this.sortColumn,
        this.sortAscending,
        this.paginator.pageSize,
        this.paginator.pageIndex,
        false
      )
      .then((res) => {
        this.processPageResult(res);
        this.totalItems = res.totalItems;
        this.countChange.emit(this.totalItems);
        this.parseFetchedItems(res.items);
        this.items = res.items;
      })
      .finally(() => {
        this.loading = false;
      });
  }

  private parseFetchedItems(items: SubProjectImmoAppointment[]): void {
    items.forEach((item) => {
      this.setComponentHelperFields(item);

      if (item.tourItem != null) {
        item.currentConfirmedPlacementDateDisplay =
          TourHelper.getTourDateDisplay(item.tourItem);
      }
    });
  }

  private setComponentHelperFields(item: SubProjectImmoAppointment): void {
    if (item.project.ipBoards != null && item.project.ipBoards.length > 0) {
      item.project.firstIpBoard = item.project.ipBoards[0];
    }

    if (item.projectPlacement == null) {
      return;
    }

    this.calculateStatusClass(item);
  }

  private calculateStatusClass(item: SubProjectImmoAppointment): void {
    if (item.projectPlacement.appointmentFailure) {
      item.statusClass = 'appointment-failure';
    } else if (
      item.tourItem != null &&
      item.tourItem.tour != null &&
      item.tourItem.tour.date != null
    ) {
      this.calculateStatusClassForDate(item, item.tourItem.tour.date);
    } else if (item.deadline != null) {
      this.calculateStatusClassForDate(item, item.deadline);
    }
  }

  private calculateStatusClassForDate(
    item: SubProjectImmoAppointment,
    date: Date
  ): void {
    const itemMoment = DateHelper.parseMoment(date);
    if (itemMoment.isSameOrBefore(this.today, 'day')) {
      item.statusClass = 'on-hold';
    } else if (
      itemMoment.isSame(
        DateHelper.parseMoment(this.today).add(1, 'days'),
        'day'
      )
    ) {
      item.statusClass = 'collab';
    }
  }

  public openProject(subProject: SubProjectImmoAppointment): void {
    this.router.navigate(
      [
        '/job',
        subProject.project == null ? null : subProject.project.id,
        subProject.id,
      ],
      { fragment: 'appointment' }
    );
  }

  // Override
  public openTour(subProject: SubProjectImmoAppointment): void {
    if (
      this.allPlacers == null ||
      subProject.tourItem == null ||
      subProject.tourItem.tour == null ||
      subProject.tourItem.tour.placer == null ||
      subProject.tourItem.tour.date == null
    ) {
      return;
    }

    this.modalService.openComponentInModal(
      EmployeeTourEditModalComponent,
      [
        { key: 'placer', value: subProject.tourItem.tour.placer },
        { key: 'tourId', value: subProject.tourItem.tour.id },
        {
          key: 'tourDate',
          value: moment(subProject.tourItem.tour.date),
        },
        { key: 'placerOptions', value: this.allPlacers },
      ],
      null,
      { minWidth: '97vw', height: '95vh' }
    );
  }
}
